import {
  SW_ANALYSIS_TYPES_REQUIRING_OCCUPANTS,
  SW_OCCUPANT_POSITION_MAP,
  GENDER,
  SW_AIRBAG_DEPLOYED
} from '@demandproai/shared';
import XInput from './XInput';
import XSelect from './XSelect';
import RenderIf from '../render-if/Renderif';
import { useSilentWitnessContext } from '../../contexts/SilentWitnessContext';
import { silentWitnessClasses } from './silentWitnessClasses';
import { cn } from "../../utils/classNames";

const POSITION_OPTIONS = Object.values(SW_OCCUPANT_POSITION_MAP);
const GENDER_OPTIONS = Object.values(GENDER).map((value) => ({ value, text: value }));
const AIRBAG_OPTIONS = Object.values(SW_AIRBAG_DEPLOYED).map((value) => ({ value, text: value }));

const sectionWrapperClasses = 'mb-4';

const groupHeaderClasses = 'mb-2 pb-2';

const subtitleClasses = 'text-body text-sm mb-3';

const emptyPlaintiffClasses = 'text-body';

const occupantCardClasses = 'p-3 mb-3';

const occupantNameClasses = 'mb-2';

const formGridClasses = 'grid grid-cols-12 gap-2';

const fieldColumnClasses = 'col-span-12 md:col-span-3';

const fullColumnClasses = 'col-span-12';

const checkboxClasses = 'mr-2';

const SilentWitnessOccupantsSection = () => {
  const {
    analysisType,
    plaintiffOccupantRows,
    updateOccupantInput
  } = useSilentWitnessContext();

  const isRelevant = SW_ANALYSIS_TYPES_REQUIRING_OCCUPANTS.has(analysisType);

  return (
    <RenderIf shouldRender={isRelevant}>
      <div className={sectionWrapperClasses}>
        <h6 className={cn(groupHeaderClasses, silentWitnessClasses.groupHeader)}>
          Occupants (biomechanics)
        </h6>

        <p className={subtitleClasses}>
          Fill out height, weight, seat position, seatbelt, and airbag for each plaintiff. Missing data will downgrade the report to accident-only.
        </p>

        <RenderIf shouldRender={plaintiffOccupantRows.length === 0}>
          <div className={emptyPlaintiffClasses}>
            No plaintiff clients on this case.
          </div>
        </RenderIf>

        {plaintiffOccupantRows.map((row) => (
          <div key={row.index} className={cn(occupantCardClasses, silentWitnessClasses.itemCard)}>
            <div className={occupantNameClasses}>
              <strong>{row.clientName || `Client ${row.index + 1}`}</strong>
            </div>

            <div className={formGridClasses}>
              <div className={fieldColumnClasses}>
                <XInput
                  label="Age"
                  type="number"
                  step="1"
                  min="0"
                  value={row.age}
                  onChange={(event) =>
                    updateOccupantInput({
                      clientIndex: row.index,
                      field: 'age',
                      value: event.target.value
                    })
                  }
                />
              </div>

              <div className={fieldColumnClasses}>
                <XSelect
                  label="Gender"
                  value={row.gender}
                  options={GENDER_OPTIONS}
                  onChange={(event) =>
                    updateOccupantInput({
                      clientIndex: row.index,
                      field: 'gender',
                      value: event.target.value
                    })
                  }
                />
              </div>

              <div className={fieldColumnClasses}>
                <XInput
                  label="Height (in)"
                  type="number"
                  value={row.heightInches}
                  onChange={(event) =>
                    updateOccupantInput({
                      clientIndex: row.index,
                      field: 'heightInches',
                      value: event.target.value
                    })
                  }
                />
              </div>

              <div className={fieldColumnClasses}>
                <XInput
                  label="Weight (lbs)"
                  type="number"
                  step="1"
                  min="0"
                  value={row.weightLbs}
                  onChange={(event) =>
                    updateOccupantInput({
                      clientIndex: row.index,
                      field: 'weightLbs',
                      value: event.target.value
                    })
                  }
                />
              </div>

              <div className={fieldColumnClasses}>
                <XSelect
                  label="Seat position"
                  value={row.position}
                  emptyOption={false}
                  options={POSITION_OPTIONS}
                  onChange={(event) =>
                    updateOccupantInput({
                      clientIndex: row.index,
                      field: 'position',
                      value: event.target.value
                    })
                  }
                />
              </div>

              <div className={fieldColumnClasses}>
                <XSelect
                  label="Airbag deployed"
                  value={row.airbagDeployed}
                  emptyOption={false}
                  options={AIRBAG_OPTIONS}
                  onChange={(event) =>
                    updateOccupantInput({
                      clientIndex: row.index,
                      field: 'airbagDeployed',
                      value: event.target.value
                    })
                  }
                />
              </div>

              <div className={fullColumnClasses}>
                <label className={silentWitnessClasses.labelText}>
                  <input
                    type="checkbox"
                    className={checkboxClasses}
                    checked={!!row.seatbeltWorn}
                    onChange={(event) =>
                      updateOccupantInput({
                        clientIndex: row.index,
                        field: 'seatbeltWorn',
                        value: event.target.checked
                      })
                    }
                  />
                  Seatbelt worn
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </RenderIf>
  );
};

export default SilentWitnessOccupantsSection;
